import type { AuthResponse, RefreshResponse, SendCodeResponse, SignupResponse } from './types';

export interface AuthApiConfig {
  baseURL: string;
  getRecaptchaToken?: () => Promise<string>;
}

/**
 * The API returns errors as `{ error: { message } }`, so reading `body.message`
 * alone yields undefined and `body.error` stringifies to "[object Object]".
 * Callers map on the message text, so the real one has to be pulled out.
 */
async function errorMessageFrom(response: Response, fallback: string): Promise<string> {
  const body: unknown = await response.json().catch(() => null);
  if (body && typeof body === 'object') {
    const { error, message } = body as { error?: unknown; message?: unknown };
    if (typeof message === 'string' && message) return message;
    if (typeof error === 'string' && error) return error;
    if (error && typeof error === 'object') {
      const nested = (error as { message?: unknown }).message;
      if (typeof nested === 'string' && nested) return nested;
    }
  }
  return fallback;
}

export class AuthApi {
  private baseURL: string;
  private getRecaptchaToken?: () => Promise<string>;

  constructor(config: AuthApiConfig) {
    this.baseURL = config.baseURL;
    this.getRecaptchaToken = config.getRecaptchaToken;
  }

  async sendCode(phoneNumber: string, recaptchaToken?: string): Promise<SendCodeResponse> {
    let token = recaptchaToken;
    if (!token && this.getRecaptchaToken) {
      try {
        token = await this.getRecaptchaToken();
      } catch (error) {
        console.warn('reCAPTCHA token not available:', error);
      }
    }

    const response = await fetch(`${this.baseURL}/auth/send-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        phoneNumber,
        ...(token ? { recaptchaToken: token } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(await errorMessageFrom(response, 'Failed to send code'));
    }

    return response.json();
  }

  async verifyCode(
    phoneNumber: string,
    sessionInfo: string,
    code: string
  ): Promise<AuthResponse> {
    const response = await fetch(`${this.baseURL}/auth/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phoneNumber, sessionInfo, code }),
    });

    if (!response.ok) {
      throw new Error(await errorMessageFrom(response, 'Failed to verify code'));
    }

    return response.json();
  }

  async signupVerify(
    phoneNumber: string,
    sessionInfo: string,
    code: string
  ): Promise<SignupResponse> {
    const response = await fetch(`${this.baseURL}/auth/signup/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phoneNumber, sessionInfo, code }),
    });

    if (!response.ok) {
      throw new Error(await errorMessageFrom(response, 'Failed to create account'));
    }

    return response.json();
  }

  async refresh(): Promise<RefreshResponse | null> {
    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.status === 401) {
      return null;
    }

    if (!response.ok) {
      throw new Error(await errorMessageFrom(response, 'Failed to refresh session'));
    }

    return response.json();
  }

  async logout(): Promise<void> {
    await fetch(`${this.baseURL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  }
}
